import React from "react";
import Link from "next/link";
import isBrowser from "../../helpers/isBrowser";
import { AnyObject } from "../../models";

/**
 * An internal utility wrapper to handle internal/external links for clickable components.
 *
 * If `link` is provided, the string is considered internal if it begins with # or /
 * all other strings are considered external.
 *
 * If no link is provided, the component is rendered with no wrapper.
 */

type Props = {
  link?: string;
  disabled?: boolean;
  target?: string;
  title?: string;
  style?: AnyObject;
  children: React.ReactNode;
};

const LinkWrapper: React.FC<Props> = ({
  disabled = false,
  link = undefined,
  title = undefined,
  target = undefined,
  children,
  style,
}) => {
  // line height stops the a tag from doing weird things
  const linkProps = {
    target: undefined,
    rel: undefined,
    title,
    style: {
      lineHeight: 0,
      textDecoration: "none",
      ...(disabled && { cursor: "not-allowed", pointerEvents: "none" as const }),
      ...style,
    },
  };

  function isSameHost() {
    if (isBrowser()) {
      try {
        const url = new URL(link);
        return url.host === window?.location?.host;
      } catch {
        return link.includes(window?.location?.host);
      }
    }
    return false;
  }
  function isLingoHost() {
    return link.includes(".lingoapp.com");
  }
  function isSpecialScheme() {
    const specialSchemes = ["mailto:", "tel:", "sms:"];
    return specialSchemes.some(s => link.startsWith(s));
  }

  let role = "externalLink",
    url = link;
  if (!link) {
    role = "action";
  } else if (isSpecialScheme() || target) {
    role = "externalLink";
    linkProps.target = target || "_blank";
    linkProps.rel = "nofollow noopener noreferrer";
  } else if ((isSameHost() || link.startsWith("/")) && link.includes("#")) {
    if (isSameHost()) {
      // Removes host from url (Workaround for internal markdown links passed into HashLink)
      //eslint-disable-next-line no-useless-escape
      url = url.replace(/^.*\/\/[^\/]+/, "");
    }
    role = "internalHashLink";
  } else if (link.startsWith("/") || link.startsWith("#")) {
    role = "internalLink";
  } else if (isSameHost()) {
    role = "internalLink";
    const idx = link.indexOf(window.location.host) + window.location.host.length;
    url = link.substring(idx);
  } else {
    role = "externalLink";
    if (!isLingoHost()) {
      // Open non-lingo links in a new window
      linkProps.target = "_blank";
      linkProps.rel = "nofollow noopener noreferrer";
    }
    if (!link.includes("//")) {
      url = `//${link}`;
    }
  }
  if (disabled) {
    return children;
  }

  switch (role) {
    case "externalLink":
      return (
        <a {...linkProps} href={url}>
          {children}
        </a>
      );
    case "internalLink":
      return (
        <Link {...linkProps} href={url}>
          {children}
        </Link>
      );
    case "internalHashLink":
      return (
        <Link {...linkProps} href={url}>
          {children}
        </Link>
      );
    default:
      return <>{children}</>;
  }
};

export default LinkWrapper;
